import {
  averageSeriesValue,
  formatChartTooltipDate,
  formatReportValue,
  isTimeSeriesSeries,
  sumSeries,
  type ReportSeriesPoint,
} from '@/app/reports/components/report-chart-utils';

export type TrendDirection = 'up' | 'down' | 'flat';

export type ReportInsightStat = {
  id: string;
  labelKey: string;
  value: number;
  display: string;
  deltaPct?: number;
};

export type ReportInsightBullet = {
  textKey: string;
  params: Record<string, string | number>;
};

export type ReportInsights = {
  stats: ReportInsightStat[];
  bullets: ReportInsightBullet[];
  direction: TrendDirection;
};

type InsightInput = {
  focus: string;
  series: ReportSeriesPoint[];
  currency?: string;
  isPercentage?: boolean;
};

const pctChange = (from: number, to: number): number | undefined => {
  if (from === 0) return to === 0 ? 0 : undefined;
  return ((to - from) / Math.abs(from)) * 100;
};

const directionFromDelta = (delta?: number): TrendDirection => {
  if (delta === undefined || Math.abs(delta) < 2) return 'flat';
  return delta > 0 ? 'up' : 'down';
};

const formatValue = (value: number, currency?: string, isPercentage?: boolean) =>
  formatReportValue(value, currency, isPercentage);

const pointLabel = (point: ReportSeriesPoint) => point.label ?? formatChartTooltipDate(point.date);

const splitHalves = (series: ReportSeriesPoint[]) => {
  const mid = Math.max(1, Math.floor(series.length / 2));
  return { first: series.slice(0, mid), second: series.slice(mid) };
};

const extrema = (series: ReportSeriesPoint[]) =>
  series.reduce(
    (acc, point) => ({
      peak: point.value > acc.peak.value ? point : acc.peak,
      low: point.value < acc.low.value ? point : acc.low,
    }),
    { peak: series[0], low: series[0] },
  );

const segmentLeader = (series: ReportSeriesPoint[]) => {
  const sorted = [...series].sort((a, b) => b.value - a.value);
  const total = sumSeries(series);
  const top = sorted[0];
  const share = total > 0 ? (top.value / total) * 100 : 0;
  const top3 = sumSeries(sorted.slice(0, 3));
  return { top, share, top3Share: total > 0 ? (top3 / total) * 100 : 0, total };
};

const stat = (
  id: string,
  labelKey: string,
  value: number,
  currency?: string,
  isPercentage?: boolean,
  deltaPct?: number,
): ReportInsightStat => ({
  id,
  labelKey,
  value,
  display: formatValue(value, currency, isPercentage),
  deltaPct,
});

const timeSeriesInsights = (input: InsightInput): ReportInsights => {
  const { series, currency, isPercentage } = input;
  const total = sumSeries(series);
  const avg = averageSeriesValue(series);
  const { first, second } = splitHalves(series);
  const delta = pctChange(sumSeries(first), sumSeries(second));
  const { peak, low } = extrema(series);
  const headline = isPercentage ? series[series.length - 1]?.value ?? 0 : total;
  const headlineKey = isPercentage ? 'insights.stats.latest' : 'insights.stats.periodTotal';
  return {
    direction: directionFromDelta(delta),
    stats: [
      stat('headline', headlineKey, headline, currency, isPercentage, delta),
      stat('average', 'insights.stats.average', avg, currency, isPercentage),
      stat('peak', 'insights.stats.peak', peak.value, currency, isPercentage),
      stat('low', 'insights.stats.low', low.value, currency, isPercentage),
    ],
    bullets: [
      { textKey: 'insights.bullets.trendDirection', params: { direction: directionFromDelta(delta) } },
      {
        textKey: 'insights.bullets.peakPeriod',
        params: { label: pointLabel(peak), value: formatValue(peak.value, currency, isPercentage) },
      },
      {
        textKey: 'insights.bullets.periodChange',
        params: { delta: delta !== undefined ? Math.round(delta) : '—' },
      },
    ],
  };
};

const rateInsights = (input: InsightInput): ReportInsights => {
  const { series, isPercentage } = input;
  const latest = series[series.length - 1]?.value ?? 0;
  const first = series[0]?.value ?? 0;
  const pointDelta = (latest - first) * (isPercentage !== false ? 100 : 1);
  const { peak, low } = extrema(series);
  return {
    direction: directionFromDelta(pointDelta),
    stats: [
      stat('latest', 'insights.stats.latestRate', latest, undefined, isPercentage ?? true, pointDelta),
      stat('start', 'insights.stats.periodStart', first, undefined, isPercentage ?? true),
      stat('peak', 'insights.stats.maxRate', peak.value, undefined, isPercentage ?? true),
      stat('low', 'insights.stats.minRate', low.value, undefined, isPercentage ?? true),
    ],
    bullets: [
      {
        textKey: 'insights.bullets.rateEvolution',
        params: { delta: Math.round(pointDelta * 10) / 10 },
      },
      {
        textKey: 'insights.bullets.rateRange',
        params: {
          min: formatValue(low.value, undefined, true),
          max: formatValue(peak.value, undefined, true),
        },
      },
    ],
  };
};

const breakdownInsights = (input: InsightInput): ReportInsights => {
  const { series, currency, isPercentage } = input;
  const { top, share, top3Share, total } = segmentLeader(series);
  const runner = [...series].sort((a, b) => b.value - a.value)[1];
  const gap = runner ? top.value - runner.value : 0;
  return {
    direction: 'flat',
    stats: [
      stat('total', 'insights.stats.total', total, currency, isPercentage),
      stat('topShare', 'insights.stats.topShare', share, undefined, true),
      stat('topValue', 'insights.stats.topSegment', top.value, currency, isPercentage),
      stat('concentration', 'insights.stats.top3Share', top3Share, undefined, true),
    ],
    bullets: [
      {
        textKey: 'insights.bullets.dominantSegment',
        params: { name: pointLabel(top), share: Math.round(share) },
      },
      {
        textKey: 'insights.bullets.segmentGap',
        params: {
          leader: pointLabel(top),
          gap: formatValue(gap, currency, isPercentage),
        },
      },
    ],
  };
};

const latencyInsights = (input: InsightInput): ReportInsights => {
  const { series } = input;
  const latest = series[series.length - 1]?.value ?? 0;
  const avg = averageSeriesValue(series);
  const { first, second } = splitHalves(series);
  const delta = pctChange(averageSeriesValue(first), averageSeriesValue(second));
  return {
    direction: directionFromDelta(delta),
    stats: [
      stat('latest', 'insights.stats.latest', latest),
      stat('average', 'insights.stats.average', avg, undefined, false, delta),
      stat('peak', 'insights.stats.peak', extrema(series).peak.value),
      stat('low', 'insights.stats.low', extrema(series).low.value),
    ],
    bullets: [
      { textKey: 'insights.bullets.latencyTrend', params: { direction: directionFromDelta(delta) } },
      { textKey: 'insights.bullets.latencyAverage', params: { value: formatValue(avg) } },
    ],
  };
};

const defaultInsights = (input: InsightInput): ReportInsights =>
  isTimeSeriesSeries(input.series) ? timeSeriesInsights(input) : breakdownInsights(input);

const FOCUS_BUILDERS: Record<string, (input: InsightInput) => ReportInsights> = {
  trend: timeSeriesInsights,
  rate: rateInsights,
  comparison: breakdownInsights,
  composition: breakdownInsights,
  snapshot: breakdownInsights,
  geographic: breakdownInsights,
  ranking: breakdownInsights,
  diagnostic: breakdownInsights,
  cyclical: timeSeriesInsights,
  funnel: breakdownInsights,
  financial: timeSeriesInsights,
  latency: latencyInsights,
};

export const computeReportInsights = (input: InsightInput): ReportInsights =>
  (FOCUS_BUILDERS[input.focus] ?? defaultInsights)(input);
